import { Link } from 'expo-router';
import { useCallback, useEffect, useRef } from 'react';
import {
  Animated,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  type ViewProps,
  ViewStyle,
} from 'react-native';

import { useDoublePointsMonday } from '@fhs/loyalty/src/hooks/use-double-points-monday';
import {
  Icon2xPoints,
  IconChallenge,
  IconChevronRight,
  IconNewChallengeBadge,
  IconRewards,
  IconRewardsAndOffers,
  IconRewardsAndOffersWithDivider,
  Pressable,
  Text,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { useMenuClickEvents } from '../analytics';
import { getOffersAndRewardsHref } from '../utils';

type IconConfig = {
  type: 'challenge' | 'rewardsAndOffersWithDivider' | 'rewardsAndOffers';
  containerStyle: 'icon' | 'legacy';
  iconProps?: {
    size?: number;
    color?: string;
  };
};

function getOffersIconConfig({
  enableRewardsTiers,
  enableOngoingChallengeCard,
  isMobile,
}: {
  enableRewardsTiers: boolean;
  enableOngoingChallengeCard: boolean;
  isMobile: boolean;
}): IconConfig {
  // Rewards Tiers enabled
  if (enableRewardsTiers) {
    if (enableOngoingChallengeCard) {
      return {
        type: 'challenge',
        containerStyle: 'icon',
      };
    }
    return {
      type: 'rewardsAndOffersWithDivider',
      containerStyle: 'icon',
      iconProps: { size: 90 },
    };
  }

  // Ongoing Challenge Card enabled (without Rewards Tiers)
  if (enableOngoingChallengeCard) {
    if (isMobile) {
      return {
        type: 'rewardsAndOffers',
        containerStyle: 'legacy',
        iconProps: {
          size: 30,
          color: tokens.colors.$houseYellow,
        },
      };
    }
    return {
      type: 'challenge',
      containerStyle: 'icon',
    };
  }

  // Default case
  return {
    type: 'rewardsAndOffers',
    containerStyle: 'legacy',
    iconProps: { size: isMobile ? 30 : 40 },
  };
}

type MenuOffersLinkProps = {
  style?: ViewProps['style'];
};

const FADE_DURATION_MS = 1000;
const isNotWeb = Platform.OS !== 'web';

export function MenuOffersLink(props: MenuOffersLinkProps) {
  const mqStyles = useMqStyles();
  const isMobile = useMqSelect({ $ltDesktop: true }, false);
  const enableRewardsTiers = useFlag('enable-rewards-tiers');
  const enableOngoingChallengeCard = useFlag('enable-challenge-ui');
  const { isDoublePointsMondayOn } = useDoublePointsMonday();
  const { noStoreSelected } = useStoreContext();
  const offersAndRewardsHref = getOffersAndRewardsHref(enableRewardsTiers, noStoreSelected);

  const badgeFadeIn = useRef(new Animated.Value(isDoublePointsMondayOn ? 1 : 0)).current;

  useEffect(() => {
    if (!isDoublePointsMondayOn) {
      return;
    }

    Animated.timing(badgeFadeIn, {
      toValue: 1,
      duration: FADE_DURATION_MS,
      useNativeDriver: isNotWeb,
    }).start();
  }, [isDoublePointsMondayOn, badgeFadeIn]);

  const renderOffersIcon = useCallback(
    ({ dividerColor }: { dividerColor?: string }) => {
      if (isDoublePointsMondayOn) {
        return (
          <Animated.View style={[animatedStyles.badgeFull, { opacity: badgeFadeIn }]}>
            <Icon2xPoints />
          </Animated.View>
        );
      }

      const config = getOffersIconConfig({
        enableRewardsTiers,
        enableOngoingChallengeCard,
        isMobile,
      });

      switch (config.type) {
        case 'challenge':
          return (
            <View style={mqStyles.iconContainer}>
              <IconChallenge />
            </View>
          );
        case 'rewardsAndOffersWithDivider':
          return (
            <View style={mqStyles.iconContainer}>
              <IconRewardsAndOffersWithDivider
                size={config.iconProps?.size}
                dividerColor={dividerColor}
              />
            </View>
          );
        case 'rewardsAndOffers':
          return (
            <View style={mqStyles.legacyIconContainer}>
              <IconRewardsAndOffers size={config.iconProps?.size} color={config.iconProps?.color} />
            </View>
          );
      }
    },
    [
      isDoublePointsMondayOn,
      badgeFadeIn,
      enableRewardsTiers,
      enableOngoingChallengeCard,
      isMobile,
      mqStyles.iconContainer,
      mqStyles.legacyIconContainer,
    ]
  );

  const renderRewardsIcon = useCallback(() => {
    return (
      <View style={mqStyles.legacyIconContainer}>
        <IconRewards size={isMobile ? 30 : 40} />
      </View>
    );
  }, [isMobile, mqStyles.legacyIconContainer]);

  return (
    <View style={mqStyles.menuOfferContainer}>
      <LinkContent
        link={offersAndRewardsHref}
        text={enableRewardsTiers ? 'Offers & Rewards' : 'Offers'}
        style={props.style}
        badge={
          enableOngoingChallengeCard && isMobile && !enableRewardsTiers ? (
            <View style={mqStyles.badge}>
              <IconNewChallengeBadge />
            </View>
          ) : undefined
        }
        renderLeftIcon={renderOffersIcon}
        textHoveredColor={enableOngoingChallengeCard ? tokens.colors.$black : undefined}
        hoveredStyle={
          enableOngoingChallengeCard ? { borderColor: tokens.colors.$houseYellow } : undefined
        }
      />
      {!enableRewardsTiers && (
        <LinkContent
          link={routes.rewards}
          text="Rewards"
          style={props.style}
          renderLeftIcon={renderRewardsIcon}
        />
      )}
    </View>
  );
}

const LinkContent = ({
  link,
  text,
  style,
  badge,
  renderLeftIcon,
  textHoveredColor = tokens.colors.$houseRed,
  hoveredStyle,
}: {
  link: string;
  text: string;
  style: ViewProps['style'];
  badge?: React.ReactNode;
  renderLeftIcon: (props: { dividerColor?: string }) => React.ReactNode;
  textHoveredColor?: string;
  hoveredStyle?: StyleProp<ViewStyle>;
}) => {
  const mqStyles = useMqStyles();
  const { logMenuClickEvent } = useMenuClickEvents();
  const isMobile = useMqSelect({ $ltDesktop: true }, false);
  return (
    <Link href={link} asChild>
      <Pressable
        accessibilityLabel={text}
        accessibilityRole="link"
        borderRadius={8}
        hoveredStyle={[styles.isHoveredPressable, hoveredStyle]}
        style={StyleSheet.flatten([mqStyles.container, style])}
        onPress={() => {
          logMenuClickEvent({ text });
        }}
        {...(Platform.OS === 'web' ? ({ 'aria-label': text } as const) : {})}
      >
        {pressableState => (
          <>
            {badge && badge}
            <View style={mqStyles.leftContainer}>
              {renderLeftIcon({
                dividerColor: pressableState.hovered
                  ? tokens.colors.$houseRed
                  : tokens.colors.$black,
              })}
              <Text.Heading
                type="three"
                style={[
                  mqStyles.offersAndRewardsText,
                  {
                    color: pressableState.hovered ? textHoveredColor : tokens.colors.$black,
                  },
                ]}
              >
                {text}
              </Text.Heading>
            </View>

            <IconChevronRight
              aria-hidden
              size={isMobile ? 20 : 30}
              color={pressableState.hovered ? textHoveredColor : tokens.colors.$black}
            />
          </>
        )}
      </Pressable>
    </Link>
  );
};

const styles = StyleSheet.create({
  isHoveredPressable: {
    borderColor: tokens.colors.$houseRedOpacity30,
  },
  isHoveredText: {
    color: tokens.colors.$houseRed,
  },
});

const animatedStyles = StyleSheet.create({
  badgeFull: {
    height: '100%',
  },
});

const useMqStyles = createMqStyles({
  menuOfferContainer: {
    $base: {
      flexDirection: 'row',
      flexGrow: 1,
      flexBasis: 0,
      gap: 24,
    },
    $ltDesktop: {
      gap: 12,
    },
  },
  container: {
    $base: {
      alignItems: 'center',
      height: 80,
      flexDirection: 'row',
      borderRadius: 8,
      borderColor: tokens.colors.$black10,
      borderWidth: 1,
      paddingRight: 24,
      flex: 1,
    },
    $ltDesktop: {
      paddingRight: 10,
    },
  },
  offersAndRewardsText: {
    $base: {
      paddingHorizontal: 20,
    },
    $ltDesktop: {
      paddingHorizontal: 8,
    },
  },
  iconContainerPadding: {
    $base: {
      paddingLeft: 22,
    },
    $ltDesktop: {
      paddingLeft: 16,
    },
  },
  leftContainer: {
    $base: {
      flexDirection: 'row',
      flexGrow: 1,
      flexBasis: 0,
      gap: 8,
      alignItems: 'center',
      height: '100%',
      overflow: 'hidden',
    },
    $ltDesktop: {
      gap: 4,
    },
  },
  iconContainer: {
    $base: {
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  legacyIconContainer: {
    $base: {
      paddingLeft: 24,
    },
    $ltDesktop: {
      paddingLeft: 10,
    },
  },
  badge: {
    $base: {
      position: 'absolute',
      top: -8,
      paddingLeft: 12,
    },
  },
});
